"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import SanityBadgeList from "@/app/(site)/_components/Molecules/GCBadge/SanityBadgeList"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { useProductDisplayInfo } from "@/app/(site)/_components/Organisms/Cards/ProductCards/helper"
import { type ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { getAccessibleImageAlt } from "@/src/utils/a11y/getAccessibleImageAlt"
import { resolveSanityInjectBadges } from "@/src/utils/badges/resolveSanityInjectBadges"
import { clsx } from "@/src/utils/clsx"
import { getProductVariantLinkLabel } from "@/src/utils/product/getProductVariantLinkLabel"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { t } from "@/translations"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

type RegularProductCardProps = {
    productVariants: ProductData[]
    listName?: string
    listId?: string
    listType?: string
    listHierarchy?: string
    index?: number
    imageSizes?: string
    isAboveTheFold?: boolean
}

const RegularProductCard = ({
    productVariants,
    listName = "Product List",
    listId = "plp",
    listType = "plp",
    listHierarchy,
    index,
    imageSizes,
    isAboveTheFold
}: RegularProductCardProps) => {
    const locale = usePathname().split("/")[1] ?? ""
    const langCode = getLangCode(locale) ?? ""
    const [isHovering, setIsHovering] = useState(false)
    const siteContext = useSiteContext()
    const {
        setCurrentVariant,
        uri,
        badges,
        colorVariantsAmount,
        sortedColor,
        name,
        extraColorsCount,
        price,
        priceBeforeDiscount,
        anyVariantisAvailable,
        showAsOnSale,
        showAsNew,
        currentProduct,
        initialImageMaybeWithOverride,
        hoverImage,
        productImageAlt
    } = useProductDisplayInfo(productVariants)
    const [currentBadges, setCurrentBadges] = useState<string[]>(badges || [])

    const [currentSanityBadgeJson, setCurrentSanityBadgeJson] = useState<
        string | undefined
    >(productVariants?.[0]?.sanity_inject_badge_json)

    useEffect(() => {
        setCurrentSanityBadgeJson(productVariants?.[0]?.sanity_inject_badge_json)
    }, [productVariants])

    const sanityBadges = resolveSanityInjectBadges(
        currentSanityBadgeJson,
        langCode || "en",
        locale
    )

    const handleChangeVariant = useCallback(
        (product: ProductData) => {
            setCurrentSanityBadgeJson(product.sanity_inject_badge_json)
            setCurrentVariant({
                media: product?.media || [],
                name: product.name,
                price: product.price,
                priceBeforeDiscount: product.priceBeforeDiscount,
                available: product.available,
                uri: product.uri,
                showAsOnSale: product.showAsOnSale,
                showAsNew: product.showAsNew
            })

            setCurrentBadges([
                ...(product.showAsNew ? ["NEW"] : []),
                ...(product.showAsOnSale ? ["SALE"] : []),
                ...(product.combinedBadges || [])
            ])
        },
        [setCurrentVariant]
    )

    useEffect(() => {
        setCurrentBadges([
            ...(showAsNew ? ["NEW"] : []),
            ...(showAsOnSale ? ["SALE"] : []),
            ...(badges || [])
        ])
    }, [showAsNew, showAsOnSale, badges])

    const handleMouseEnter = () => {
        setIsHovering(true)
    }

    const handleMouseLeave = () => {
        setIsHovering(false)
    }

    const handleProductClick = () => {
        // Find the product variant that matches the current display
        const productToTrack =
            productVariants.find(
                p =>
                    p.name === currentProduct?.name ||
                    p.color_swatch?.color_name ===
                        currentProduct?.color_swatch?.color_name
            ) || productVariants[0]

        if (productToTrack) {
            import("@/lib/utils/tracking/gtmEvents").then(({ selectItemEvent }) => {
                selectItemEvent(
                    productToTrack,
                    { size: undefined, index },
                    listName,
                    listId,
                    siteContext,
                    listType,
                    listHierarchy
                )
            })
        }
    }

    return (
        <div
            className="group relative"
            data-search-result-id={productVariants?.[0]?.search_result_id}
            data-product-listing-result-id={
                productVariants?.[0]?.product_listing_result_id
            }
            data-recommendation-id={productVariants?.[0]?.recommendation_id}
        >
            <DynamicLink
                href={uri}
                prefetch={false}
                onClick={handleProductClick}
                aria-label={getProductVariantLinkLabel(
                    langCode,
                    productVariants[0]?.name ?? name,
                    productVariants[0]?.color_swatch?.color_name
                )}
                className={FOCUS_VISIBLE_INSET_CLASS}
            >
                <div>
                    <div className="bg-[#f4f5f5]">
                        {sanityBadges.length > 0 ? (
                            <SanityBadgeList
                                badges={sanityBadges}
                                className="absolute left-0 right-0 top-0 z-[5] m-8 flex flex-wrap gap-4"
                            />
                        ) : (
                            Array.isArray(currentBadges) &&
                            currentBadges.length > 0 && (
                                <div className="absolute left-0 right-0 top-0 z-[5] flex flex-wrap gap-2">
                                    {currentBadges.map((badge, index) => (
                                        <Badge key={index} badge={badge} />
                                    ))}
                                </div>
                            )
                        )}
                        <div className="relative mb-16 aspect-square overflow-hidden">
                            <BaseImage
                                fill
                                key={`${initialImageMaybeWithOverride}-${name}`}
                                isAboveTheFold={isAboveTheFold}
                                src={
                                    isHovering && hoverImage
                                        ? hoverImage
                                        : initialImageMaybeWithOverride
                                }
                                alt={getAccessibleImageAlt(productImageAlt, name)}
                                className="object-contain"
                                sizes={imageSizes}
                                onMouseEnter={() => hoverImage && handleMouseEnter()}
                                onMouseLeave={() => hoverImage && handleMouseLeave()}
                            />
                        </div>
                    </div>
                    <div className="w-full">
                        <Text size="small" weight="regular" className="md:text-16">
                            {name}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="flex flex-wrap gap-x-8 break-words pb-8 text-main-dark-grey md:text-16"
                        >
                            <span>{price}</span>
                            {showAsOnSale && (
                                <span className="line-through">
                                    {priceBeforeDiscount}
                                </span>
                            )}
                        </Text>
                    </div>
                </div>
            </DynamicLink>
            <div className="flex h-38 w-full">
                <div className="hidden md:group-hover:block">
                    <div className="flex w-full items-center">
                        {sortedColor.map((swatch, index) => (
                            <DynamicLink
                                href={`/product/${swatch.uri}`}
                                className={clsx(
                                    "cursor-pointer",
                                    FOCUS_VISIBLE_INSET_CLASS
                                )}
                                onMouseEnter={() => handleChangeVariant(swatch.product)}
                                onMouseLeave={() =>
                                    handleChangeVariant(productVariants[0])
                                }
                                onClick={() => {
                                    import("@/lib/utils/tracking/gtmEvents").then(
                                        ({ selectItemEvent }) => {
                                            selectItemEvent(
                                                swatch.product,
                                                { size: undefined, index: index },
                                                listName,
                                                listId,
                                                siteContext,
                                                listType,
                                                listHierarchy
                                            )
                                        }
                                    )
                                }}
                                key={index}
                                prefetch={false}
                                aria-label={getProductVariantLinkLabel(
                                    langCode,
                                    swatch.name ?? "",
                                    swatch.colorName
                                )}
                            >
                                {swatch.color ? (
                                    <div className="m-4 h-24 w-24 rotate-[-50deg]">
                                        <div className="relative h-24 w-24">
                                            {swatch.image ? (
                                                <Image
                                                    alt=""
                                                    src={swatch.image.url}
                                                    width={100}
                                                    height={100}
                                                    className="absolute inset-0 z-20 rounded-full"
                                                    style={{
                                                        clipPath:
                                                            "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="absolute inset-0 rounded-full"
                                                    style={{
                                                        clipPath:
                                                            "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                        backgroundColor:
                                                            swatch.lens?.hex ??
                                                            swatch.color
                                                    }}
                                                ></div>
                                            )}
                                            <div
                                                className="absolute inset-0 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                    backgroundColor: swatch.color
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                ) : null}
                            </DynamicLink>
                        ))}
                        {extraColorsCount > 0 && (
                            <div>
                                <Text
                                    size="small"
                                    weight="regular"
                                    className="text-main-dark-grey md:text-16"
                                >
                                    +{extraColorsCount} {t(langCode, "common.more")}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
                {colorVariantsAmount > 0 && (
                    <div>
                        <Text
                            size="small"
                            weight="regular"
                            className="text-main-dark-grey md:text-16 md:group-hover:hidden"
                        >
                            {colorVariantsAmount} {t(langCode, "product_page.colors")}
                        </Text>
                    </div>
                )}
            </div>
            {!anyVariantisAvailable && (
                <Text
                    size="small"
                    weight="regular"
                    className="text-main-black md:text-16"
                >
                    {t(langCode, "product_page.out_of_stock")}
                </Text>
            )}
        </div>
    )
}

export default RegularProductCard
